import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import StickButtonsBloc from '../../blocs/common/StickButtonsBloc';
import { APP_URLS, ERROR_MESSAGES } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import { initSmsSignatureCode, listPreContractDocuments } from "../../services/CustomerApiService";
import LoaderModal from "../../modals/common/LoaderModal";
import MgpMessage from "../../elements/MgpMessage/MgpMessage";
import {AxiosError} from "axios";
import {IApiErrors, IPreContractDocument} from "../../context/model";
import PreContractDocumentComponent from "./PreContractDocumentComponent";
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import PhoneNumberConfirmModal from '../../modals/common/PhoneNumberConfirmModal';

type InfoSignatureStep0FormData = {
  phoneNumber: string;
  isForeignMobile: boolean
}

export default function InfoSignatureStep0FormComponent() {

  const navigate = useNavigate();

  const {
    state: { proposition, preContractDocuments, jwtToken, updatedProposition, showModal },
    dispatch,
  } = useAppState();

  const [contractDocuments, setContractDocuments] = useState<IPreContractDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>();
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .when('isForeignMobile', {
          is: false,
          then: Yup.string()
                  .required(ERROR_MESSAGES.requiredMsg)
                  .matches(/^(0[6-7])([ .-]?\d{2}){4}$/, ERROR_MESSAGES.invalidFrenchMobile)
                  .min(14, ERROR_MESSAGES.invalidMsg),
      })
      .when('isForeignMobile', {
          is: true,
          then: Yup.string()
                  .required(ERROR_MESSAGES.requiredMsg)
                  .matches(/^(?:\+|00)[0-9]\d{1,14}$/, ERROR_MESSAGES.invalidForeignMobile)
                  .test("number-is-french", ERROR_MESSAGES.invalidFrenchMobileWithIndicator, async (value, values) => {
                    if(!value) {
                      return false;
                    }
                    const isFrenchNumber = value.startsWith("0033") || value.startsWith("+33");
                    return !isFrenchNumber;
                  }),      
          }),
  });

  const {
    register,
    getValues,
    setValue,
    watch,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<InfoSignatureStep0FormData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      isForeignMobile: updatedProposition?.isForeignMobile ? updatedProposition.isForeignMobile : false,
      phoneNumber: updatedProposition?.phoneNumber && !updatedProposition?.isForeignMobile ? updatedProposition.phoneNumber.replace(/[^\dA-Z]/g, '').replace(/(.{2})/g, '$1 ').trim(): updatedProposition?.phoneNumber && updatedProposition?.isForeignMobile ? updatedProposition?.phoneNumber : "",
    },
    resolver: yupResolver(validationSchema),
  });

  useEffect(() => {
    if (preContractDocuments?.length) {
      setContractDocuments(preContractDocuments);
      return;
    }
    const listDocuments = async () => {
      const res = await listPreContractDocuments(jwtToken!.accessToken, proposition!.propositionId);
      setLoading(false);
      dispatch({type: 'LIST_PRE_CONTRACT_DOCUMENT', preContractDocuments: res.data});
      setContractDocuments(res.data);
    }

    setErrorMsg(null);
    setLoading(true);
    listDocuments()
      .catch(error => {
        setLoading(false);
        const err = error as AxiosError;
        console.log(err);
        if(err.response && err.response.status === 400) {
          const errorData = err.response?.data as IApiErrors;
          setErrorMsg("Erreur: " + errorData.message);
        } else {
          setErrorMsg("Une erreur technique est survenue ");
        }
    });
  }, []);

  const onNextAction = async () => {
    const notAcceptedDocs = contractDocuments.filter(ele => !ele.accepted)
    if (notAcceptedDocs?.length) {
      setErrorMsg('Veuillez prendre connaissance et accepter tous les documents précontractuels');
      return;
    }

    setOpenConfirmModal(true);
    
  }

  const onConfirmAction =async () => {
    setLoading(true);
    try {
      const token = jwtToken!.accessToken;
      const phoneNumber = getValues("phoneNumber").replace(/ /g, '');
      const res = await initSmsSignatureCode(token, proposition!.propositionId, phoneNumber);

      dispatch({type: 'NEXT_INFO_STEP', path: APP_URLS.infoSignatureStep1Page});
      dispatch({type: 'INIT_SMS', sessionUid: res.data.sessionUid, phoneNumberSMS: phoneNumber});
      navigate({
        pathname: APP_URLS.infoSignatureStep1Page,
        search: `?id=${proposition!.propositionId}`,
      }, {replace:true});
     }catch (error) {
      setLoading(false);
      setOpenConfirmModal(false);
      const err = error as AxiosError;
      console.log(err);
      if(err.response && err.response.status === 400) {
        const errorData = err.response?.data as IApiErrors;
        setErrorMsg("Erreur: " + errorData.message);
      } else {
        setErrorMsg("Une erreur technique est survenue ");
      }
    }
  }

  const onPreviousAction = () => {
    dispatch({type: 'BACK_INFO_STEP', path: APP_URLS.recapPage});
    navigate({
      pathname: APP_URLS.recapPage,
      search: `?id=${proposition!.propositionId}`,
    });
  }

  const onDocumentUpdate = (doc: IPreContractDocument) => {
    const updatedDocuments = contractDocuments.map(ele => {
      if (ele.key === doc.key) {
        return doc;
      } else {
        return ele;
      }
    })

    dispatch({type: 'LIST_PRE_CONTRACT_DOCUMENT', preContractDocuments: updatedDocuments});
    setContractDocuments(updatedDocuments);
  }

     const handleChangeisForeignMobileCheckbox = (e: any) => {
        setValue("isForeignMobile", e.target.checked);
        clearErrors("phoneNumber");

        if (e.target.checked) {
            setValue("phoneNumber", "+");     
        } else {
            setValue("phoneNumber", "");
        }   
      }
  
      const handleChangePhoneNumber = (e: any) => {
          const regexSpace = new RegExp("(.{"+ 2 + "})", "g");
          let target = e.currentTarget, position = target.selectionEnd, length = target.value.length;
          target.value = target.value.replace(/\s/g, '').replace(regexSpace, '$1 ').trim();
          target.selectionEnd = position += ((target.value.charAt(position - 1) === ' ' && target.value.charAt(length - 1) === ' ' && length !== target.value.length) ? 1 : 0);
          setValue && setValue("phoneNumber", target.value);
      }
  return (
    <>
      <div>
        {errorMsg && <MgpMessage type="danger" message={errorMsg} hasCloseBtn={true}/>}
      </div>
      <form>
        {contractDocuments.map((ele, i) => {
          return <PreContractDocumentComponent key={i} preContractDocument={ele} onDocumentUpdate={onDocumentUpdate} handleSetMessageError={(message: string)=> setErrorMsg(message)} />;
        })}
      </form>
      {loading && <LoaderModal hasMessage={true}/>}
      
      {!showModal && <StickButtonsBloc showNextButton={true} showPreviousButton={true} previousAction={onPreviousAction} nextAction={onNextAction}/>}

      { 
        openConfirmModal && 
        <PhoneNumberConfirmModal 
          isOpen={openConfirmModal}
          onConfirmAction={handleSubmit(onConfirmAction)} 
          onCloseModal={() => setOpenConfirmModal(false)}
          register={register}
          errors={errors}
          watch={watch}
          handleChangeisForeignMobileCheckbox={handleChangeisForeignMobileCheckbox}
          handleChangePhoneNumber={handleChangePhoneNumber}
        />
      }
  </>
  )
}
